import { useEffect } from "react";
import {
  Modal,
  Form,
  FormItem,
  useForm,
  InputNumber,
  Message,
} from "@/frontend/components/common";
import { fixtureService } from "@/frontend/services";
import type { FixtureResponseDTO, FixtureStatusResponseDTO, UpdateFixtureRequestDTO } from "@/shared/dto";
import { useState } from "react";

interface FixtureEditModalProps {
  open: boolean;
  fixture: FixtureResponseDTO | null;
  fixtureStatuses: FixtureStatusResponseDTO[];
  requiresFairPlay: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export function FixtureEditModal({
  open,
  fixture,
  fixtureStatuses,
  requiresFairPlay,
  onCancel,
  onSuccess,
}: FixtureEditModalProps) {
  const [form] = useForm();
  const [saving, setSaving] = useState(false);

  const hasGroup = Boolean(fixture?.groupId);

  useEffect(() => {
    if (fixture && open) {
      form.setFieldsValue({
        homeScore: fixture.homeScore,
        awayScore: fixture.awayScore,
        homePenalties: fixture.homePenalties,
        awayPenalties: fixture.awayPenalties,
        homeFairPlayScore: fixture.homeFairPlayScore ?? undefined,
        awayFairPlayScore: fixture.awayFairPlayScore ?? undefined,
      });
    }
  }, [fixture, open, form]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async () => {
    if (!fixture) return;

    try {
      setSaving(true);
      const values = await form.validateFields();
      const finalStatus = fixtureStatuses.find((status) => status.isFinal);

      const updateData: UpdateFixtureRequestDTO = {
        homeScore: values.homeScore,
        awayScore: values.awayScore,
        homePenalties: values.homePenalties,
        awayPenalties: values.awayPenalties,
        homeFairPlayScore: values.homeFairPlayScore,
        awayFairPlayScore: values.awayFairPlayScore,
        statusId: finalStatus?.id ?? "finished",
        updateStandings: hasGroup,
      };

      const response = await fixtureService.update(fixture.id, updateData);

      if (response.success) {
        Message.success("Partido actualizado correctamente");
        form.resetFields();
        onSuccess();
      } else {
        Message.error("Error al actualizar el partido: " + response.message);
      }
    } catch (error) {
      Message.error("Error al actualizar el partido");
      console.error("Error al actualizar el partido:", error);
    } finally {
      setSaving(false);
    }
  };

  const title = hasGroup 
    ? "Editar Partido de Grupo" 
    : "Editar Partido de Eliminatoria";

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      onOk={handleSave}
      confirmLoading={saving}
      okText="Guardar"
      cancelText="Cancelar"
      width={500}
    >
      <Form form={form} layout="vertical" className="mt-4">
        {/* Goles siempre visibles */}
        <div className="grid grid-cols-2 gap-4">
          <FormItem
            name="homeScore"
            label="Goles Local"
            rules={[{ required: true, message: "Requerido" }]}
          >
            <InputNumber min={0} className="w-full" />
          </FormItem>
          <FormItem
            name="awayScore"
            label="Goles Visitante"
            rules={[{ required: true, message: "Requerido" }]}
          >
            <InputNumber min={0} className="w-full" />
          </FormItem>
        </div>

        {/* Penales solo para knockout */}
        {!hasGroup && (
          <div className="grid grid-cols-2 gap-4">
            <FormItem name="homePenalties" label="Penales Local">
              <InputNumber min={0} className="w-full" />
            </FormItem>
            <FormItem name="awayPenalties" label="Penales Visitante">
              <InputNumber min={0} className="w-full" />
            </FormItem>
          </div>
        )}

        {/* Fair Play solo para grupos con configuración de tarjetas */}
        {hasGroup && requiresFairPlay && (
          <div className="grid grid-cols-2 gap-4">
            <FormItem
              name="homeFairPlayScore"
              label="Fair Play Local"
              tooltip="Puntuación de fair play (tarjetas)"
            >
              <InputNumber min={0} className="w-full" />
            </FormItem>
            <FormItem
              name="awayFairPlayScore"
              label="Fair Play Visitante"
              tooltip="Puntuación de fair play (tarjetas)"
            >
              <InputNumber min={0} className="w-full" />
            </FormItem>
          </div>
        )}

        {/* Mensaje informativo para partidos de grupo */}
        {hasGroup && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mt-2">
              Al guardar, se recalcularán los puntos y estadísticas en la tabla
              de posiciones.
            </p>
          </div>
        )}
      </Form>
    </Modal>
  );
}
